import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { Box, useTheme, Button, InputBase, IconButton } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import { useEffect, useState } from "react";
import { getNomina } from "../../services/marcaciones.services"; // Ensure this imports your updated backend function
import SearchIcon from "@mui/icons-material/Search";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import CustomToolbar from "../../utils/CustomToolbar"; // Assuming this is still needed for DataGrid toolbar
import ExcelJS from "exceljs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker"; // Explicitly import DatePicker

const Nomina = () => {
  const [data, setData] = useState([]); // Stores the fetched data
  const [searchValue, setSearchValue] = useState(""); // For search input
  const [filteredData, setFilteredData] = useState([]); // Data after search filter
  const [startDate, setStartDate] = useState(dayjs()); // Start date for data fetch
  const [endDate, setEndDate] = useState(dayjs()); // End date for data fetch
  const [isLoading, setIsLoading] = useState(false); // Loading state

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  // Define columns for DataGrid
  const columns = [
    { field: "user_id", headerName: "ID_USUARIO", flex: 0.3 },
    {
      field: "nombre",
      headerName: "NOMBRE",
      flex: 0.5,
      cellClassName: "name-column--cell",
    },
    {
      field: "worked_days",
      headerName: "DÍAS TRABAJADOS", // Updated header for clarity
      flex: 0.3,
      type: "number",
      cellClassName: "name-column--cell",
    },
    {
      field: "salario_ips",
      headerName: "SALARIO_IPS",
      flex: 0.2,
      type: "number",
      cellClassName: "name-column--cell",
    },
    {
      field: "nocturno",
      headerName: "BONO NOCTURNO", // Updated header for clarity
      flex: 0.2,
      type: "number",
      cellClassName: "name-column--cell",
    },
    {
      field: "total_income",
      headerName: "SALARIO_TAX", // Updated header for clarity
      flex: 0.2,
      type: "number",
      headerAlign: "right",
      align: "right",
    },
    {
        field: "aporte_obrero",
        headerName: "Aporte Obrero (9%)",
        flex: 0.5,
        type: "number",
        headerAlign: "right",
        align: "right",
        valueFormatter: ({ value }) => value.toLocaleString('es-ES', { minimumFractionDigits: 2 }),
    },
    {
        field: "aporte_patronal",
        headerName: "Aporte Patronal (16.5%)",
        flex: 0.5,
        type: "number",
        headerAlign: "right",
        align: "right",
        valueFormatter: ({ value }) => value.toLocaleString('es-ES', { minimumFractionDigits: 2 }),
    },
    {
        field: "total_aporte",
        headerName: "Total Aporte (25.5%)",
        flex: 0.5,
        type: "number",
        headerAlign: "right",
        align: "right",
        valueFormatter: ({ value }) => value.toLocaleString('es-ES', { minimumFractionDigits: 2 }),
    },
    {
        field: "month",
        headerName: "MES", // Updated header for clarity
        flex: 0.1,
        type: "number",
        headerAlign: "right",
        align: "right",
    },
    {
        field: "year",
        headerName: "Año", // Updated header for clarity
        flex: 0.3,
        type: "number",
        headerAlign: "right",
        align: "right",
    },
  ];

  // Effect to fetch data when dates change
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);

        const formattedStart = startDate.format("YYYY-MM-DD");
        const formattedEnd = endDate.format("YYYY-MM-DD");

        const nominaDatos = await getNomina(formattedStart, formattedEnd);

        // Map backend data to frontend fields (ensure all used fields are mapped)
        const mappedData = nominaDatos.map((row) => {
            const totalIncome = Number(row.total_income) || 0;
            const aporteObrero = totalIncome * 0.09;
            const aportePatronal = totalIncome * 0.165;
            const totalAporte = aporteObrero + aportePatronal;
          
            return {
              user_id: row.user_id,
              nombre: row.nombre,
              month: row.month,
              year: row.year,
              worked_days: Number(row.worked_days) || 0,
              salario_ips: Number(row.salario_ips) || 0,
              nocturno: Number(row.nocturno) || 0,
              total_income: totalIncome,
              aporte_obrero: aporteObrero,
              aporte_patronal: aportePatronal,
              total_aporte: totalAporte,
            };
          });

        setData(mappedData);
        setFilteredData(mappedData); // Initialize filteredData with all data
      } catch (error) {
        console.error("Error fetching nomina data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [startDate, endDate]); // Dependency array: re-run when startDate or endDate changes

  // Effect to filter data based on search value
  useEffect(() => {
    if (searchValue.trim() === "") {
      setFilteredData(data); // If search is empty, show all data
    } else {
      const searchFiltered = data.filter((item) =>
        item.nombre.toLowerCase().includes(searchValue.toLowerCase())
      );
      setFilteredData(searchFiltered);
    }
  }, [searchValue, data]); // Dependency array: re-run when searchValue or data changes

  // Handler for search input change
  const handleSearchChange = (event) => {
    setSearchValue(event.target.value);
  };

  // Export to Excel using ExcelJS
  const handleExport = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Resumen Nomina"); // Changed sheet name for clarity

    // Define columns for Excel export
    // Ensure these keys match the data you are mapping in `mappedData`
    worksheet.columns = [
      { header: "Nombre y Apellido", key: "nombre", width: 25 },
      // If 'grupo_descripcion' is not fetched, remove this or add it to backend query
      // { header: "Sector", key: "grupo_descripcion", width: 20 },
      { header: "Días Trabajados", key: "worked_days", width: 18 },
      { header: "Salario IPS", key: "salario_ips", width: 15 },
      { header: "Bono Nocturno", key: "nocturno", width: 15 },
      { header: "Total Ingreso", key: "total_income", width: 18 },
    ];

    // Add rows to the worksheet
    worksheet.addRows(
      filteredData.map((row) => ({
        nombre: row.nombre,
        // grupo_descripcion: row.grupo_descripcion, // If included
        worked_days: row.worked_days,
        salario_ips: row.salario_ips,
        nocturno: row.nocturno,
        total_income: row.total_income,
      }))
    );

    // Styling for header row
    worksheet.getRow(1).eachCell((cell) => {
      cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF143F04" }, // Green color
      };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });

    // Styling for all cells (borders)
    worksheet.eachRow((row) => {
      row.eachCell((cell) => {
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });
    });

    // Generate and download the Excel file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Nomina_Resumen_${startDate.format('YYYYMMDD')}_${endDate.format('YYYYMMDD')}.xlsx`; // Dynamic filename
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Header title="Resumen Nomina IPS" subtitle="Resumen Nomina IPS" />
        <Box display="flex" alignItems="center" gap={2}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DemoContainer components={["DatePicker", "DatePicker"]}>
              <DatePicker
                label="Fecha de Inicio"
                value={startDate}
                onChange={(newValue) => setStartDate(dayjs(newValue))}
                format="DD/MM/YYYY"
              />
              <DatePicker
                label="Fecha de Fin"
                value={endDate}
                onChange={(newValue) => setEndDate(dayjs(newValue))}
                format="DD/MM/YYYY"
              />
            </DemoContainer>
          </LocalizationProvider>

          <Button
            variant="contained"
            onClick={handleExport}
            sx={{
              backgroundColor: "#143f04",
              "&:hover": { backgroundColor: "#36a60f" },
              height: "3rem",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              gap: 1,
              color: "white", // Ensure text color is white
            }}
          >
            <img
              src="https://cdn-icons-png.flaticon.com/512/732/732220.png"
              alt="Excel Icon"
              style={{ width: 24, height: 24 }}
            />
            Exportar
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          height: "75vh",
          "& .MuiDataGrid-root": { border: "none" },
          "& .MuiDataGrid-cell": { borderBottom: "none" },
          "& .name-column--cell": { color: colors.greenAccent[300] },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[900],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.blueAccent[900],
          },
          "& .MuiCheckbox-root": {
            color: `${colors.greenAccent[200]} !important`,
          },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
            color: `${colors.grey[100]} !important`,
          },
        }}
      >
        <InputBase
          sx={{ mb: 1, ml: 1, width: "30%" }}
          placeholder="Buscar"
          value={searchValue}
          onChange={handleSearchChange}
          startAdornment={<SearchIcon />}
        />

        <DataGrid
          loading={isLoading}
          rows={filteredData}
          columns={columns}
          getRowId={(row) => `${row.user_id}-${row.year}-${row.month}`}
          components={{ Toolbar: CustomToolbar }}

          pagination
          pageSize={100}
          rowsPerPageOptions={[100]}
          autoHeight={false}
        />
      </Box>
    </Box>
  );
};

export default Nomina;